<template>
  <div class="event-info-panel">
    <button class="close-btn" @click="$emit('close')" aria-label="Stäng">×</button>

    <!-- Okänd slug (t.ex. gammal felskriven länk) -->
    <div v-if="chosenEvent && chosenEvent.notFound" class="panel-content">
      <h2 class="panel-title">Eventet hittades inte</h2>
      <p class="panel-description">
        Länken kan vara felaktig eller så har eventet tagits bort. Bläddra
        bland kommande konserter i listan.
      </p>
    </div>

    <div v-else-if="chosenEvent" class="panel-content">
      <div class="panel-image-wrapper">
        <img :src="chosenEvent.imageUrl" :alt="chosenEvent.title" class="panel-image" />
      </div>

      <h2 class="panel-title">{{ chosenEvent.title }}</h2>

      <!-- Passerade event: sidan lever kvar (delade länkar ska inte dö),
           men vi flaggar tydligt att det redan varit -->
      <p v-if="chosenEvent.hasPassed" class="passed-notice">
        Denna konsert har redan varit. Kolla in kommande spelningar i listan!
      </p>

      <div class="panel-meta">
        <div class="meta-row">
          <span class="meta-label">DATUM</span>
          <span class="meta-value">{{ chosenEvent.date.split("T")[0] }}</span>
        </div>
        <div class="meta-row">
          <span class="meta-label">PLATS</span>
          <span class="meta-value meta-value--venue">{{ chosenEvent.venue }}</span>
        </div>
        <div v-if="chosenEvent.eventPrice" class="meta-row">
          <span class="meta-label">INTRÄDE</span>
          <span class="meta-value">{{ chosenEvent.eventPrice }}</span>
        </div>
        <div v-if="shouldShowGenre(chosenEvent)" class="meta-row">
          <span class="meta-label">GENRE</span>
          <span class="meta-value">{{ chosenEvent.genre }}</span>
        </div>
      </div>

      <p v-if="chosenEvent.eventInfo" class="panel-description">
        {{ chosenEvent.eventInfo }}
      </p>

      <div class="panel-actions">
        <a
          v-if="chosenEvent.ticketLink && !chosenEvent.hasPassed"
          :href="normalizedTicketLink"
          target="_blank"
          rel="noopener noreferrer"
          class="ticket-btn"
          @click="trackOutboundClick('ticket')"
        >
          Köp biljett
        </a>

        <a
          v-if="chosenEvent.link"
          :href="normalizedEventLink"
          target="_blank"
          rel="noopener noreferrer"
          class="event-link-btn"
          @click="trackOutboundClick('event_page')"
        >
          Till eventet
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
            <polyline points="15 3 21 3 21 9"/>
            <line x1="10" y1="14" x2="21" y2="3"/>
          </svg>
        </a>

        <button
          v-if="chosenEvent.slug"
          class="share-btn"
          @click="shareEvent"
          :aria-label="copied ? 'Länk kopierad' : 'Dela eventet'"
          title="Dela"
        >
          {{ copied ? "Länk kopierad!" : "Dela" }}
          <svg v-if="!copied" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="18" cy="5" r="3"/>
            <circle cx="6" cy="12" r="3"/>
            <circle cx="18" cy="19" r="3"/>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
          </svg>
          <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { shouldShowGenre } from "@/utils/genreDisplay";

export default {
  name: "EventInfo",
  emits: ["close"],
  props: ["chosenEvent"],
  computed: {
    normalizedTicketLink() {
      return this.normalizeUrl(this.chosenEvent?.ticketLink);
    },
    normalizedEventLink() {
      return this.normalizeUrl(this.chosenEvent?.link);
    },
  },
  methods: {
    shouldShowGenre,
    /**
     * Dela eventet: Web Share API där det finns (mobil), annars kopiera
     * länken till urklipp. URL:en byggs från window.location.origin så
     * delning fungerar även på preview-domäner.
     */
    async shareEvent() {
      const url = `${window.location.origin}/event/${this.chosenEvent.slug}`;

      if (this.$gtag) {
        this.$gtag.event("share", {
          event_category: this.chosenEvent?.venue,
          event_label: this.chosenEvent?.title,
          method: navigator.share ? "web_share" : "clipboard",
        });
      }

      try {
        if (navigator.share) {
          await navigator.share({
            title: this.chosenEvent.title,
            text: `${this.chosenEvent.title} – ${this.chosenEvent.venue}`,
            url,
          });
        } else {
          await navigator.clipboard.writeText(url);
          this.copied = true;
          setTimeout(() => {
            this.copied = false;
          }, 2000);
        }
      } catch (err) {
        // Användaren avbröt delningsdialogen — inget fel
      }
    },
    trackOutboundClick(type) {
      if (this.$gtag) {
        this.$gtag.event("outbound_click", {
          event_category: this.chosenEvent?.venue,
          event_label: this.chosenEvent?.title,
          link_type: type,
        });
      }
    },
    normalizeUrl(link) {
      if (!link) return "";
      if (link.startsWith("http://") || link.startsWith("https://")) {
        return link;
      }
      return "https://" + link;
    },
  },
  data() {
    return {
      copied: false,
    };
  },
};
</script>

<style scoped>
.event-info-panel {
  background: var(--color-bg);
  border-right: 1px solid var(--color-border);
  height: 100%;
  width: 100%;
  padding: 2rem;
  overflow-y: auto;
  position: relative;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.75rem;
  cursor: pointer;
  color: var(--color-text-muted);
  line-height: 1;
  padding: 0.25rem 0.5rem;
  transition: color 0.15s ease;
}

.close-btn:hover {
  color: var(--color-accent);
}

.passed-notice {
  margin: 0 0 1rem;
  padding: 0.6rem 0.9rem;
  font-size: 0.85rem;
  color: var(--color-text-muted);
  border: 1px solid var(--color-border);
  border-left: 3px solid var(--color-accent);
}

.panel-content {
  margin-top: 2rem;
}

.panel-image-wrapper {
  width: 100%;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  margin-bottom: 1.25rem;
}

.panel-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: grayscale(100%);
  display: block;
}

.panel-title {
  font-family: "Playfair Display", serif;
  font-weight: 900;
  font-size: 1.5rem;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  line-height: 1.15;
  color: var(--color-text);
  margin-bottom: 1rem;
}

.panel-meta {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 1.25rem;
  border-top: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
  padding: 0.75rem 0;
}

.meta-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
}

.meta-label {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--color-text-faint);
  white-space: nowrap;
}

.meta-value {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--color-meta-value);
  text-align: right;
}

.meta-value--venue {
  text-transform: capitalize;
}

.panel-description {
  font-size: 0.875rem;
  line-height: 1.65;
  color: var(--color-text-secondary);
  margin-bottom: 1.5rem;
}

.panel-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  align-items: stretch;
}

.ticket-btn,
.event-link-btn,
.share-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.65rem 1.5rem;
  text-decoration: none;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.ticket-btn {
  background: var(--color-accent);
  color: #fff;
}

.ticket-btn:hover {
  background: #aa0e00;
}

.event-link-btn {
  background: transparent;
  color: var(--color-text);
  border: 1px solid var(--color-text);
}

.event-link-btn:hover {
  background: var(--color-text);
  color: var(--color-bg);
}

/* Textknapp utan border, samma färgton som "Till eventet" — texten byts
   till "Länk kopierad!" vid kopiering på desktop */
.share-btn {
  background: transparent;
  color: var(--color-text);
  border: none;
  cursor: pointer;
  padding: 0.65rem 0.75rem;
}

.share-btn:hover {
  color: var(--color-accent);
}
</style>

